import { createTheme, ThemeOptions } from '@mui/material/styles';
import colors from './colors';

interface IThemeOptions extends ThemeOptions {}

declare module '@mui/material/Typography' {
  interface TypographyVariants {
    display1: React.CSSProperties,
    display2: React.CSSProperties,
    display3: React.CSSProperties,
    h1: React.CSSProperties,
    h2: React.CSSProperties,
    h3: React.CSSProperties,
    h4: React.CSSProperties,
    h5: React.CSSProperties,
    h6: React.CSSProperties,
    subhead1: React.CSSProperties,
    subhead2: React.CSSProperties,
    button: React.CSSProperties,
    bodyOne: React.CSSProperties,
    bodyTwo: React.CSSProperties,
    caption: React.CSSProperties,
    overline: React.CSSProperties,
    labelSmall: React.CSSProperties,
    tableHeader: React.CSSProperties,
    tableCell: React.CSSProperties,

    displayL: React.CSSProperties,
    displayM: React.CSSProperties,
    displayS: React.CSSProperties,
    hL: React.CSSProperties,
    hM: React.CSSProperties,
    hS: React.CSSProperties,
    titleL: React.CSSProperties,
    titleM: React.CSSProperties,
    titleS: React.CSSProperties,
    labelL: React.CSSProperties,
    labelM: React.CSSProperties,
    labelS: React.CSSProperties,
    bodyL: React.CSSProperties,
    bodyM: React.CSSProperties,
    bodyS: React.CSSProperties,
  }

  interface TypographyVariantsOptions {
    display1?: React.CSSProperties,
    display2?: React.CSSProperties,
    display3?: React.CSSProperties,
    h1?: React.CSSProperties,
    h2?: React.CSSProperties,
    h3?: React.CSSProperties,
    h4?: React.CSSProperties,
    h5?: React.CSSProperties,
    h6?: React.CSSProperties,
    subhead1?: React.CSSProperties,
    subhead2?: React.CSSProperties,
    button?: React.CSSProperties,
    bodyOne?: React.CSSProperties,
    bodyTwo?: React.CSSProperties,
    caption?: React.CSSProperties,
    overline?: React.CSSProperties,
    labelSmall?: React.CSSProperties,
    tableHeader?: React.CSSProperties,
    tableCell?: React.CSSProperties,

    displayL?: React.CSSProperties,
    displayM?: React.CSSProperties,
    displayS?: React.CSSProperties,
    headlineL?: React.CSSProperties,
    headlineM?: React.CSSProperties,
    headlineS?: React.CSSProperties,
    titleL?: React.CSSProperties,
    titleM?: React.CSSProperties,
    titleS?: React.CSSProperties,
    labelL?: React.CSSProperties,
    labelM?: React.CSSProperties,
    labelS?: React.CSSProperties,
    bodyL?: React.CSSProperties,
    bodyM?: React.CSSProperties,
    bodyS?: React.CSSProperties,
  }

  interface TypographyPropsVariantOverrides {
    display1: true,
    display2: true,
    display3: true,
    h1:true,
    h2:true,
    h3:true,
    h4:true,
    h5:true,
    h6:true,
    subhead1: true,
    subhead2: true,
    button: true,
    bodyOne: true,
    bodyTwo: true,
    caption: true,
    overline: true,
    labelSmall: true,
    tableHeader: true,
    tableCell: true,

    displayL: true,
    displayM: true,
    displayS: true,
    headlineL: true,
    headlineM: true,
    headlineS: true,
    titleL: true,
    titleM: true,
    titleS: true,
    labelL: true,
    labelM: true,
    labelS: true,
    bodyL: true,
    bodyM: true,
    bodyS: true,
  }
}

const themeStyles = {
  palette: {
    primary: {
      main: colors.primaryMain,
      light: colors.primaryLight,
      dark: colors.primaryDark,
    },
    secondary: {
      main: colors.secondaryMain,
      light: colors.secondaryLight,
      dark: colors.secondaryDark,
    },
    error: {
      main: colors.error,
    },
    warning: {
      main: colors.warningYellow,
    },
    info: {
      main: colors.infoBlue,
    },
    success: {
      main: colors.successGreen,
    },
    common: {
      ...colors,
    },
  },
  typography: {
    fontFamily: [
      'Open Sans',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
    display1: {
      fontSize: '64px',
      letterSpacing: '-0.5px',
      fontWeight: 400,
      lineHeight: '76px',
    },
    display2: {
      fontSize: '57px',
      letterSpacing: '-0.25px',
      fontWeight: 400,
      lineHeight: '64px',
    },
    display3: {
      fontSize: '45px',
      fontWeight: 400,
      lineHeight: '52px',
      letterSpacing: '0px',
    },
    h1:{
      fontSize: '36px',
      fontWeight: 400,
      lineHeight: '44px',
      letterSpacing: '0px',
    },
    h2:{
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '40px',
      letterSpacing: '0px',
    },
    h3:{
      fontSize: '28px',
      letterSpacing: '-0.01em',
      fontWeight: 400,
      lineHeight: '36px',
    },
    h4:{
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '32px',
      letterSpacing: '0px',
    },
    h5:{
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: '28px',
      letterSpacing: '0px',
    },
    h6:{
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0px',
    },
    subhead1: {
      fontSize: '16px',
      letterSpacing: '0.1px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    subhead2: {
      fontSize: '14px',
      letterSpacing: '0.1px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    button: {
      fontSize: '14px',
      letterSpacing: '0.1px',
      fontWeight: 600,
      lineHeight: '20px',
      textTransform: 'none',
    },
    bodyOne: {
      fontSize: '16px',
      letterSpacing: '0.5px',
      fontWeight: 400,
      lineHeight: '24px',
    },
    bodyTwo: {
      fontSize: '14px',
      letterSpacing: '0.25px',
      fontWeight: 400,
      lineHeight: '20px',
    },
    caption: {
      fontSize: '12px',
      letterSpacing: '0.4px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    tableHeader: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      fontStretch: '85%',
      letterSpacing: '0px',
    },
    tableCell: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      fontStretch: '85%',
      letterSpacing: '0px',
    },
    overline: {
      fontSize: '12px',
      letterSpacing: '0.5px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    labelSmall: {
      fontSize: '11px',
      letterSpacing: '0.5px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    displayL: {
      fontSize: '57px',
      letterSpacing: '-2px',
      fontWeight: 400,
      lineHeight: '64px',
    },
    displayM: {
      fontSize: '45px',
      letterSpacing: '-1px',
      fontWeight: 400,
      lineHeight: '52px',
    },
    displayS: {
      fontSize: '36px',
      letterSpacing: '-0.01em',
      fontWeight: 400,
      lineHeight: '44px',
    },
    headlineL: {
      fontSize: '32px',
      letterSpace: '-0.02em',
      fontWeight: 700,
      lineHeight: '40px',
    },
    headlineM: {
      fontSize: '28px',
      letterSpace: '-0.01em',
      fontWeight: 700,
      lineHeight: '36px',
    },
    headlineS: {
      fontSize: '24px',
      letterSpace: '-0.005em',
      fontWeight: 700,
      lineHeight: '32px',
    },
    titleL: {
      fontSize: '22px',
      letterSpace: '-0.15em',
      fontWeight: 600,
      lineHeight: '28px',
    },
    titleM: {
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '0.1px',
      lineHeight: '24px',
    },
    titleS: {
      fontSize: '14px',
      fontWeight: 600,
      letterSpacing: '0.1px',
      lineHeight: '20px',
    },
    labelL: {
      fontSize: '14px',
      fontWeight: 600,
      letterSpacing: '0.1px',
      lineHeight: '20px',
    },
    labelM: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.25px',
      lineHeight: '16px',
    },
    labelS: {
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.4px',
      lineHeight: '16px',
    },
    bodyL: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0px',
    },
    bodyM: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0px',
    },
    bodyS: {
      fontSize: '12px',
      fontWeight: 400,
      letterSpacing: '0.25px',
      lineHeight: '16px',
    },
  },
}

const theme = createTheme(themeStyles as IThemeOptions)

export default theme;
